import type { CodeableConcept, Task } from '@medplum/fhirtypes';

import {
  CLINICAL_TASK_CATEGORY_SYSTEM,
  CLINICAL_TASK_ID_SYSTEM,
} from './systems';
import {
  CLINICAL_TASK_CATEGORY_LABELS,
  CLINICAL_TASK_PRIORITIES,
  type ClinicalTaskCategory,
  type ClinicalTaskPriority,
} from './task-drafts';

/**
 * Pure readers and transitions over a Clinical Task's FHIR shape, shared
 * browser/backend (ENG-5368). The accepted-task lifecycle they encode:
 * `requested` (To do) ⇄ `completed` (Done); `cancelled` is a tombstone.
 * Review/extraction markers (review-status, derived-from-appointment) are
 * cross-agent mini-app-framework concerns and deliberately stay out.
 */

/** True if the Task is a Clinical Task (carries the clinical-task identifier),
 *  as opposed to e.g. the care-plan-generation job Task. */
export function isClinicalTask(task: Task): boolean {
  return Boolean(
    task.identifier?.some((i) => i.system === CLINICAL_TASK_ID_SYSTEM),
  );
}

/** Read the flat category code off Task.code, or null. */
export function readTaskCategory(task: Task): ClinicalTaskCategory | null {
  const code = task.code?.coding?.find(
    (c) => c.system === CLINICAL_TASK_CATEGORY_SYSTEM,
  )?.code;
  return code && code in CLINICAL_TASK_CATEGORY_LABELS
    ? (code as ClinicalTaskCategory)
    : null;
}

/**
 * Build `Task.code` for a clinical task. Per FHIR Task guidance `code.text` is
 * the task title, so the task's descriptive text rides there; the category is
 * the coding, with its human label in `display`. The one place this shape is
 * minted, so every writer stays identical. (Note: Medplum indexes both
 * `code.text` and `coding.display` into the token `:text` search column, so a
 * `code:text=` search also matches the category label — intended.)
 */
export function buildClinicalTaskCode(
  category: ClinicalTaskCategory,
  title: string,
): CodeableConcept {
  return {
    coding: [
      {
        system: CLINICAL_TASK_CATEGORY_SYSTEM,
        code: category,
        display: CLINICAL_TASK_CATEGORY_LABELS[category],
      },
    ],
    text: title,
  };
}

/**
 * Read a clinical task's title. New-shape tasks carry it in `code.text`;
 * pre-title tasks put only the category label there and kept the text in
 * `description`, so a `code.text` equal to the category's label is treated as
 * "no title" and we fall back to `description`. Returns null when neither
 * yields text (callers supply their own placeholder).
 */
export function readTaskTitle(task: Task): string | null {
  const text = task.code?.text?.trim();
  const category = readTaskCategory(task);
  const label = category ? CLINICAL_TASK_CATEGORY_LABELS[category] : null;
  if (text && text !== label) return text;
  return task.description?.trim() || text || null;
}

/** Read the native FHIR priority, or null (pre-priority tasks have none). */
export function readTaskPriority(task: Task): ClinicalTaskPriority | null {
  return task.priority && CLINICAL_TASK_PRIORITIES.includes(task.priority)
    ? task.priority
    : null;
}

/** Read the due date (restriction.period.end), or null. */
export function readTaskDueDate(task: Task): string | null {
  return task.restriction?.period?.end ?? null;
}

/** The complete/undo flip: Done ⇄ To do, every other field preserved. */
export function withTaskCompletion(task: Task, done: boolean): Task {
  return { ...task, status: done ? 'completed' : 'requested' };
}

export type TaskDueState = 'due-today' | 'past-due' | null;

/**
 * Calendar-day due state of a task relative to `now` (the caller's clock —
 * pass it in so the derivation stays pure): due-today, past-due, or null
 * for future/absent/unparseable dates.
 */
export function taskDueState(
  dueDate: string | null | undefined,
  now: Date,
): TaskDueState {
  if (!dueDate) return null;
  const due = calendarDay(dueDate);
  if (!due) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (due.getTime() === today.getTime()) return 'due-today';
  return due.getTime() < today.getTime() ? 'past-due' : null;
}

function calendarDay(value: string): Date | null {
  // Date-only strings parse as UTC midnight; build from parts so the
  // calendar day stays local.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}
